import React, { useState } from 'react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useForm } from 'react-hook-form';
import { 
  FiSave, 
  FiGlobe, 
  FiCreditCard, 
  FiMail, 
  FiShield,
  FiUser,
  FiSettings,
  FiBell,
  FiUpload,
  FiImage,
  FiTrash2,
  FiPlay
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { CurrencyDemo } from '../../components/CurrencyDemo';
import { TestInvoiceSender } from '../../components/TestInvoiceSender';
import styles from './Settings.module.css';

interface CompanySettings {
  companyName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  taxId: string;
  logo?: string;
  industry: string;
  foundedYear: string;
  employeeCount: string;
  description: string;
}

interface InvoiceSettings {
  invoicePrefix: string;
  nextInvoiceNumber: number;
  defaultDueDays: number;
  defaultTaxRate: number;
  currency: string;
  currencySymbol: string;
  language: string;
  invoiceTemplate: string;
  termsAndConditions: string;
  footerText: string;
}

interface PaymentSettings {
  stripeEnabled: boolean;
  stripePublishableKey: string;
  paypalEnabled: boolean;
  paypalClientId: string;
  bankTransferEnabled: boolean;
  bankDetails: string;
  lateFeePercentage: number;
  lateFeeMinimum: number;
}

interface NotificationSettings {
  emailNotifications: boolean;
  invoiceReminders: boolean;
  paymentConfirmations: boolean;
  overdueAlerts: boolean;
  weeklyReports: boolean;
  smsNotifications: boolean;
}

interface UserPreferences {
  theme: 'light' | 'dark' | 'auto';
  timezone: string;
  dateFormat: string;
  currencyDisplay: string;
  dashboardLayout: 'grid' | 'list';
  autoSave: boolean;
}

// Combined form data interface
interface FormData extends CompanySettings, InvoiceSettings, PaymentSettings, NotificationSettings, UserPreferences {}

export function Settings() {
  const [activeTab, setActiveTab] = useState('company');
  const [isSaving, setIsSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const { updateCurrencyPreferences } = useCurrency();

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<FormData>({
    defaultValues: {
      companyName: 'C-Band Professional Services',
      email: 'info@cband.com',
      phone: '+27 (11) 123-4567',
      website: 'https://www.cband.com',
      address: '123 Business Street',
      city: 'Johannesburg',
      state: 'Gauteng',
      zipCode: '2000',
      country: 'ZA',
      taxId: 'VAT123456789',
      industry: 'consulting',
      foundedYear: '2020',
      employeeCount: '1-10',
      description: 'Professional invoicing and accounting services for modern businesses.',
      logo: '',
      invoicePrefix: 'INV',
      nextInvoiceNumber: 1001,
      defaultDueDays: 30,
      defaultTaxRate: 15.00,
      currency: 'ZAR',
      currencySymbol: 'R',
      language: 'en',
      invoiceTemplate: 'modern',
      termsAndConditions: 'Payment is due within 30 days of invoice date. Late payments may incur additional charges.',
      footerText: 'Thank you for choosing C-Band Professional Services!',
      stripeEnabled: false,
      stripePublishableKey: '',
      paypalEnabled: false,
      paypalClientId: '',
      bankTransferEnabled: false,
      bankDetails: '',
      lateFeePercentage: 5.00,
      lateFeeMinimum: 10.00,
      emailNotifications: true,
      invoiceReminders: true,
      paymentConfirmations: true,
      overdueAlerts: true,
      weeklyReports: true,
      smsNotifications: false,
      theme: 'light',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      currencyDisplay: 'symbol',
      dashboardLayout: 'grid',
      autoSave: true
    }
  });

  const onSubmit = async (data: FormData) => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update currency preferences in context
      updateCurrencyPreferences({
        currency: data.currency,
        currencySymbol: data.currencySymbol,
        currencyDisplay: data.currencyDisplay as 'symbol' | 'code' | 'both'
      });
      
      // Log the submitted data for demonstration
      console.log('Settings submitted:', data);
      
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'company', label: 'Company Info', icon: <FiGlobe size={20} /> },
    { id: 'invoice', label: 'Invoice Settings', icon: <FiCreditCard size={20} /> },
    { id: 'payment', label: 'Payment Settings', icon: <FiCreditCard size={20} /> },
    { id: 'notifications', label: 'Notifications', icon: <FiBell size={20} /> },
    { id: 'preferences', label: 'Preferences', icon: <FiUser size={20} /> },
    { id: 'security', label: 'Security', icon: <FiShield size={20} /> },
    { id: 'testing', label: 'Testing', icon: <FiPlay size={20} /> }
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Settings</h1>
          <p className={styles.subtitle}>Configure your C-Band account and preferences</p>
        </div>
        <button 
          onClick={handleSubmit(onSubmit)} 
          disabled={isSaving}
          className={`${styles.saveButton} ${isSaving ? styles.loading : ''}`}
        >
          <FiSave size={16} />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className={styles.content}>
        {/* Tab Navigation */}
        <div className={styles.tabNavigation}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {activeTab === 'company' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Company Information</h2>
              
              {/* Company Logo Section */}
              <div className={styles.logoSection}>
                <h3 className={styles.subsectionTitle}>Company Logo</h3>
                <div className={styles.logoUpload}>
                  <div className={styles.logoPreview}>
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt="Company Logo" 
                        className={styles.logoImage}
                      />
                    ) : (
                      <div className={styles.logoPlaceholder}>
                        <FiImage size={48} />
                        <span>No logo uploaded</span>
                      </div>
                    )}
                  </div>
                  <div className={styles.logoActions}>
                    <label className={styles.uploadButton}>
                      <FiUpload size={16} />
                      Upload Logo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              const result = e.target?.result as string;
                              setLogoUrl(result);
                              setValue('logo', result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {logoUrl && (
                      <button
                        type="button"
                        className={styles.removeLogoButton}
                        onClick={() => {
                          setLogoUrl('');
                          setValue('logo', '');
                        }}
                      >
                        <FiTrash2 size={16} />
                        Remove
                      </button>
                    )}
                  </div>
                  <p className={styles.logoHelp}>
                    Recommended: 200x200px, PNG or JPG format. Max size: 2MB
                  </p>
                </div>
              </div>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Company Name *</label>
                  <input
                    type="text"
                    {...register('companyName', { required: 'Company name is required' })}
                    className={styles.input}
                    placeholder="Your Company Name"
                  />
                  {errors.companyName && (
                    <span className={styles.error}>{errors.companyName.message?.toString()}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Industry</label>
                  <select {...register('industry')} className={styles.select}>
                    <option value="">Select Industry</option>
                    <option value="technology">Technology</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="finance">Finance</option>
                    <option value="education">Education</option>
                    <option value="retail">Retail</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="consulting">Consulting</option>
                    <option value="real-estate">Real Estate</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Founded Year</label>
                  <input
                    type="number"
                    {...register('foundedYear')}
                    className={styles.input}
                    placeholder="2020"
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Employee Count</label>
                  <select {...register('employeeCount')} className={styles.select}>
                    <option value="">Select Size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Email Address *</label>
                  <input
                    type="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className={styles.input}
                    placeholder="info@company.com"
                  />
                  {errors.email && (
                    <span className={styles.error}>{errors.email.message?.toString()}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Phone Number</label>
                  <input
                    type="tel"
                    {...register('phone')}
                    className={styles.input}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Website</label>
                  <input
                    type="url"
                    {...register('website')}
                    className={styles.input}
                    placeholder="https://www.company.com"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Address</label>
                  <input
                    type="text"
                    {...register('address')}
                    className={styles.input}
                    placeholder="123 Business Street"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>City</label>
                  <input
                    type="text"
                    {...register('city')}
                    className={styles.input}
                    placeholder="City"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>State/Province</label>
                  <input
                    type="text"
                    {...register('state')}
                    className={styles.input}
                    placeholder="State"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>ZIP/Postal Code</label>
                  <input
                    type="text"
                    {...register('zipCode')}
                    className={styles.input}
                    placeholder="12345"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Country</label>
                  <select {...register('country')} className={styles.select}>
                    <option value="">Select Country</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="UK">United Kingdom</option>
                    <option value="AU">Australia</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="ZA">South Africa</option>
                    <option value="NG">Nigeria</option>
                    <option value="KE">Kenya</option>
                    <option value="GH">Ghana</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Tax ID</label>
                  <input
                    type="text"
                    {...register('taxId')}
                    className={styles.input}
                    placeholder="Tax ID or VAT Number"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Company Description</label>
                <textarea
                  {...register('description')}
                  className={styles.textarea}
                  rows={4}
                  placeholder="Brief description of your company, services, and mission..."
                />
              </div>
            </div>
          )}

          {activeTab === 'invoice' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Invoice Settings</h2>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Invoice Prefix</label>
                  <input
                    type="text"
                    {...register('invoicePrefix')}
                    className={styles.input}
                    placeholder="INV"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Next Invoice Number</label>
                  <input
                    type="number"
                    {...register('nextInvoiceNumber')}
                    className={styles.input}
                    placeholder="1001"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Default Due Days</label>
                  <input
                    type="number"
                    {...register('defaultDueDays')}
                    className={styles.input}
                    placeholder="30"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Default Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('defaultTaxRate')}
                    className={styles.input}
                    placeholder="10.00"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Currency</label>
                  <select {...register('currency')} className={styles.select}>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="ZAR">ZAR - South African Rand</option>
                    <option value="NGN">NGN - Nigerian Naira</option>
                    <option value="KES">KES - Kenyan Shilling</option>
                    <option value="GHS">GHS - Ghanaian Cedi</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                    <option value="CHF">CHF - Swiss Franc</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Currency Symbol</label>
                  <select {...register('currencySymbol')} className={styles.select}>
                    <option value="$">$ (Dollar)</option>
                    <option value="€">€ (Euro)</option>
                    <option value="£">£ (Pound)</option>
                    <option value="C$">C$ (Canadian Dollar)</option>
                    <option value="A$">A$ (Australian Dollar)</option>
                    <option value="R">R (Rand)</option>
                    <option value="₦">₦ (Naira)</option>
                    <option value="KSh">KSh (Shilling)</option>
                    <option value="₵">₵ (Cedi)</option>
                    <option value="₹">₹ (Rupee)</option>
                    <option value="¥">¥ (Yen)</option>
                    <option value="Fr">Fr (Franc)</option>
                  </select>
                  <p className={styles.helpText}>
                    This symbol will be used in invoices and quotes (e.g., $100.00)
                  </p>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Language</label>
                  <select {...register('language')} className={styles.select}>
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Invoice Template</label>
                  <select {...register('invoiceTemplate')} className={styles.select}>
                    <option value="modern">Modern</option>
                    <option value="classic">Classic</option>
                    <option value="minimal">Minimal</option>
                    <option value="professional">Professional</option>
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Terms & Conditions</label>
                <textarea
                  {...register('termsAndConditions')}
                  className={styles.textarea}
                  rows={4}
                  placeholder="Enter your terms and conditions..."
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Footer Text</label>
                <textarea
                  {...register('footerText')}
                  className={styles.textarea}
                  rows={2}
                  placeholder="Thank you for your business!"
                />
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Payment Settings</h2>
              
              <div className={styles.paymentMethod}>
                <div className={styles.methodHeader}>
                  <h3>Stripe</h3>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      {...register('stripeEnabled')}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Publishable Key</label>
                  <input
                    type="text"
                    {...register('stripePublishableKey')}
                    className={styles.input}
                    placeholder="pk_test_..."
                  />
                </div>
              </div>

              <div className={styles.paymentMethod}>
                <div className={styles.methodHeader}>
                  <h3>PayPal</h3>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      {...register('paypalEnabled')}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Client ID</label>
                  <input
                    type="text"
                    {...register('paypalClientId')}
                    className={styles.input}
                    placeholder="PayPal Client ID"
                  />
                </div>
              </div>

              <div className={styles.paymentMethod}>
                <div className={styles.methodHeader}>
                  <h3>Bank Transfer</h3>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      {...register('bankTransferEnabled')}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Bank Details</label>
                  <textarea
                    {...register('bankDetails')}
                    className={styles.textarea}
                    rows={3}
                    placeholder="Enter bank account details..."
                  />
                </div>
              </div>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Late Fee Percentage (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('lateFeePercentage')}
                    className={styles.input}
                    placeholder="5.00"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Minimum Late Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('lateFeeMinimum')}
                    className={styles.input}
                    placeholder="10.00"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Notification Settings</h2>
              
              <div className={styles.notificationGroup}>
                <h3>Email Notifications</h3>
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      {...register('emailNotifications')}
                      className={styles.checkbox}
                    />
                    Enable email notifications
                  </label>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      {...register('invoiceReminders')}
                      className={styles.checkbox}
                    />
                    Invoice reminders
                  </label>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      {...register('paymentConfirmations')}
                      className={styles.checkbox}
                    />
                    Payment confirmations
                  </label>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      {...register('overdueAlerts')}
                      className={styles.checkbox}
                    />
                    Overdue payment alerts
                  </label>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      {...register('weeklyReports')}
                      className={styles.checkbox}
                    />
                    Weekly reports
                  </label>
                </div>
              </div>

              <div className={styles.notificationGroup}>
                <h3>SMS Notifications</h3>
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      {...register('smsNotifications')}
                      className={styles.checkbox}
                    />
                    Enable SMS notifications
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>User Preferences</h2>
              
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Theme</label>
                  <select {...register('theme')} className={styles.select}>
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Timezone</label>
                  <select {...register('timezone')} className={styles.select}>
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="Europe/London">London</option>
                    <option value="Europe/Paris">Paris</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Date Format</label>
                  <select {...register('dateFormat')} className={styles.select}>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Currency Display</label>
                  <select {...register('currencyDisplay')} className={styles.select}>
                    <option value="symbol">Symbol ($)</option>
                    <option value="code">Code (USD)</option>
                    <option value="name">Name (US Dollar)</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Dashboard Layout</label>
                  <select {...register('dashboardLayout')} className={styles.select}>
                    <option value="grid">Grid</option>
                    <option value="list">List</option>
                  </select>
                </div>
              </div>

              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    {...register('autoSave')}
                    className={styles.checkbox}
                  />
                  Auto-save forms
                </label>
              </div>
              
              {/* Currency Demo */}
              <CurrencyDemo />
            </div>
          )}

          {activeTab === 'security' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Security Settings</h2>
              
              <div className={styles.securityGroup}>
                <h3>Two-Factor Authentication</h3>
                <p className={styles.description}>
                  Add an extra layer of security to your account by enabling two-factor authentication.
                </p>
                <button className={styles.securityButton}>
                  Enable 2FA
                </button>
              </div>

              <div className={styles.securityGroup}>
                <h3>Password</h3>
                <p className={styles.description}>
                  Change your account password to keep your account secure.
                </p>
                <button className={styles.securityButton}>
                  Change Password
                </button>
              </div>

              <div className={styles.securityGroup}>
                <h3>Session Management</h3>
                <p className={styles.description}>
                  View and manage your active sessions across different devices.
                </p>
                <button className={styles.securityButton}>
                  Manage Sessions
                </button>
              </div>
            </div>
          )}

          {activeTab === 'testing' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Testing</h2>
              <TestInvoiceSender />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
